import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  Switch,
} from '@mui/material';
import StableCKEditor from '../components/StableCKEditor';
import ClassicEditor from '../utils/ckeditor';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { projectsAPI, tasksAPI, timeEntriesAPI, taskStatusesAPI, usersAPI, sprintsAPI, customersAPI } from '../services/api';
import TaskEditDialog from '../components/TaskEditDialog';
import TaskCreateDialog from '../components/TaskCreateDialog';
import { toEditorHtml } from '../utils/sanitizeHtml';
import { localISODate } from '../utils/dates';
import { useAuth } from '../contexts/AuthContext';

const TasksPage = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]); // Store all projects for filtering
  const [tasks, setTasks] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [descEditorKey, setDescEditorKey] = useState(0); // leert den Editor nach dem Anlegen

  // Filter states
  const [filterCustomer] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [showInactive] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: '',
    project_id: '',
    task_id: '',
    name: '',
    description: '',
    status_id: '',
    priority: 'medium',
    assignee_id: '',
    due_date: '',
    effort_estimate: '',
    parent_task_id: '',
    sprint_id: '',
    is_timetracker_only: false,
  });

  const [editingTask, setEditingTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Time entry modal
  const [showTimeEntryModal, setShowTimeEntryModal] = useState(false);
  const [, setSelectedTask] = useState(null);
  const [timeEntryData, setTimeEntryData] = useState({
    subtask_comment: '',
    hours: '',
    date: localISODate(),
  });

  useEffect(() => {
    fetchData();
    // Load global statuses on mount
    fetchProjectData(null);
  }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter projects when customer filter changes
  useEffect(() => {
    if (filterCustomer) {
      const filtered = allProjects.filter(p => p.customer_id === parseInt(filterCustomer));
      setProjects(filtered);
      // Reset project filter if current project doesn't belong to selected customer
      const currentProject = allProjects.find(p => p.id === parseInt(filterProject));
      if (!currentProject || currentProject.customer_id !== parseInt(filterCustomer)) {
        setFilterProject('');
      }
    } else {
      setProjects(allProjects);
    }
  }, [filterCustomer, allProjects, filterProject]);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const params = showInactive ? { include_inactive: true } : {};
      const [customersRes, projectsRes, tasksRes, usersRes] = await Promise.all([
        customersAPI.getAll(),
        projectsAPI.getAll(),
        tasksAPI.getAll(params),
        usersAPI.getAll(),
      ]);
      setCustomers(customersRes.data);
      setAllProjects(projectsRes.data);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch project-specific data when project is selected (or global statuses if no project)
  const fetchProjectData = async (projectId) => {
    try {
      // Load statuses - project-specific or global
      const statusesRes = await taskStatusesAPI.getAll(projectId || null);
      const statuses = statusesRes.data || [];
      setTaskStatuses(statuses);

      // Set default status to "Offen" if available (first status with order 0)
      if (statuses.length > 0 && !formData.status_id) {
        const defaultStatus = statuses.find(s => s.order === 0) || statuses[0];
        setFormData(prev => ({
          ...prev,
          status_id: defaultStatus.id
        }));
      }

      // Load sprints only if project is selected
      if (projectId) {
        const sprintsRes = await sprintsAPI.getAll(projectId);
        setSprints(sprintsRes.data || []);
      } else {
        setSprints([]);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Projektdaten:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Load project-specific data when project changes
    if (name === 'project_id') {
      fetchProjectData(value);
    }
  };

  const handleTaskUpdated = () => {
    fetchData();
    setSuccess('Task erfolgreich aktualisiert');
  };

  const handleTaskCreated = () => {
    fetchData();
    setSuccess('Task erfolgreich erstellt');
  };

  const closeTimeEntryModal = () => {
    setShowTimeEntryModal(false);
    setSelectedTask(null);
    setTimeEntryData({
      project_id: '',
      task_id: '',
      subtask_comment: '',
      hours: '',
      date: localISODate(),
    });
  };

  const handleTimeEntryChange = (e) => {
    setTimeEntryData({
      ...timeEntryData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTimeEntrySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await timeEntriesAPI.create({
        task_id: timeEntryData.task_id,
        subtask_comment: timeEntryData.subtask_comment,
        hours: parseFloat(timeEntryData.hours),
        date: timeEntryData.date,
        user_id: user.id,
      });
      setSuccess('Zeiteintrag erfolgreich erstellt');
      closeTimeEntryModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen des Zeiteintrags');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTask(null);
    setIsCreating(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          Tasks
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Task Create Form */}
      <Card sx={{ borderRadius: 3, mb: 3, background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: '#1e3a5f', mb: 3 }}>
            Task erstellen
          </Typography>
          <Box component="form" onSubmit={async (e) => {
            e.preventDefault();
            setError('');
            setSuccess('');
            try {
              const createData = { ...formData };
              if (!createData.assignee_id) createData.assignee_id = null;
              if (!createData.due_date) createData.due_date = null;
              if (!createData.effort_estimate) createData.effort_estimate = null;
              if (!createData.sprint_id) createData.sprint_id = null;
              if (!createData.status_id) createData.status_id = null;
              if (!createData.project_id) createData.project_id = null;
              if (!createData.task_id) createData.task_id = null;

              await tasksAPI.create(createData);
              setSuccess('Task erfolgreich erstellt');
              // Reset form and reload default status
              setFormData({
                customer_id: '',
                project_id: '',
                task_id: '',
                name: '',
                description: '',
                status_id: '',
                priority: 'medium',
                assignee_id: '',
                due_date: '',
                effort_estimate: '',
                parent_task_id: '',
                sprint_id: '',
                is_timetracker_only: false,
              });
              setDescEditorKey(k => k + 1);
              fetchData();
              fetchProjectData(null); // Reload default status
            } catch (err) {
              setError(err.response?.data?.error || 'Fehler beim Erstellen des Tasks');
            }
          }} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Zeile 1: Kunde und Projekt */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 45%', minWidth: '200px' }} required>
                <InputLabel>Kunde</InputLabel>
                <Select
                  name="customer_id"
                  value={formData.customer_id || ''}
                  onChange={(e) => {
                    const customerId = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      customer_id: customerId,
                      project_id: '',
                      status_id: '',
                      sprint_id: ''
                    }));
                    if (customerId) {
                      const filtered = allProjects.filter(p => p.customer_id === parseInt(customerId));
                      setProjects(filtered);
                    } else {
                      setProjects(allProjects);
                    }
                    // Load global statuses when customer changes
                    fetchProjectData(null);
                  }}
                  label="Kunde"
                >
                  <MenuItem value="">
                    <em>Kunde auswählen</em>
                  </MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: '1 1 45%', minWidth: '200px' }} disabled={!formData.customer_id}>
                <InputLabel>Projekt (optional)</InputLabel>
                <Select
                  name="project_id"
                  value={formData.project_id}
                  onChange={(e) => {
                    const projectId = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      project_id: projectId,
                      status_id: '',
                      sprint_id: ''
                    }));
                    fetchProjectData(projectId || null);
                  }}
                  label="Projekt (optional)"
                >
                  <MenuItem value="">
                    <em>Kein Projekt (nur Zeiterfassung)</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Zeile 2: Taskname und Task-ID */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: '1 1 70%', minWidth: '300px' }}
                label="Taskname"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <TextField
                sx={{ flex: '1 1 25%', minWidth: '150px' }}
                label="Task-ID (optional)"
                name="task_id"
                value={formData.task_id}
                onChange={handleChange}
                placeholder="z.B. PROJ-123"
              />
            </Box>

            {/* Zeile 3: Nur Zeiterfassung Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_timetracker_only}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_timetracker_only: e.target.checked }))}
                  name="is_timetracker_only"
                />
              }
              label="Nur für Zeiterfassung (erscheint nicht im Board)"
            />

            {/* Zeile 4: Status, Priorität, Zugewiesen */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status_id"
                  value={formData.status_id}
                  onChange={handleChange}
                  label="Status"
                >
                  {taskStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Priorität</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label="Priorität"
                >
                  <MenuItem value="low">Niedrig</MenuItem>
                  <MenuItem value="medium">Mittel</MenuItem>
                  <MenuItem value="high">Hoch</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Zugewiesen an</InputLabel>
                <Select
                  name="assignee_id"
                  value={formData.assignee_id}
                  onChange={handleChange}
                  label="Zugewiesen an"
                >
                  <MenuItem value="">
                    <em>Nicht zugewiesen</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Zeile 5: Fälligkeitsdatum, Aufwand, Sprint */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: '1 1 30%', minWidth: '200px' }}
                label="Fälligkeitsdatum"
                name="due_date"
                type="date"
                value={formData.due_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                sx={{ flex: '1 1 30%', minWidth: '200px' }}
                label="Aufwand (Punkte)"
                name="effort_estimate"
                type="number"
                value={formData.effort_estimate}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />

              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }} disabled={!formData.project_id}>
                <InputLabel>Sprint</InputLabel>
                <Select
                  name="sprint_id"
                  value={formData.sprint_id}
                  onChange={handleChange}
                  label="Sprint"
                >
                  <MenuItem value="">
                    <em>Kein Sprint</em>
                  </MenuItem>
                  {sprints.map((sprint) => (
                    <MenuItem key={sprint.id} value={sprint.id}>
                      {sprint.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Zeile 6: Beschreibung mit CKEditor */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Beschreibung
              </Typography>
              <Box
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '& .ck-editor__editable': {
                    minHeight: '200px',
                  },
                }}
              >
                <StableCKEditor
                  key={descEditorKey}
                  editor={ClassicEditor}
                  data={toEditorHtml(formData.description || '')}
                  config={{
                    placeholder: 'Detaillierte Beschreibung der Aufgabe...',
                    toolbar: [
                      'heading', '|',
                      'bold', 'italic', 'link', '|',
                      'bulletedList', 'numberedList', '|',
                      'blockQuote', 'insertTable', '|',
                      'undo', 'redo'
                    ],
                  }}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setFormData(prev => ({
                      ...prev,
                      description: data,
                    }));
                  }}
                />
              </Box>
            </Box>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
                  },
                }}
              >
                Task erstellen
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Create/Edit Modal - Now using TaskEditDialog */}
      {showModal && isCreating && (
        <TaskCreateDialog
          key="task-create"
          open={showModal}
          onClose={closeModal}
          projects={projects}
          customers={customers}
          allProjects={allProjects}
          onTaskCreated={handleTaskCreated}
        />
      )}

      {showModal && !isCreating && editingTask && (
        <TaskEditDialog
          key={`task-edit-${editingTask?.id}`}
          open={showModal}
          onClose={closeModal}
          task={editingTask}
          projectId={editingTask?.project_id}
          onTaskUpdated={handleTaskUpdated}
        />
      )}

      {/* Time Entry Modal */}
      <Dialog
        open={showTimeEntryModal}
        onClose={closeTimeEntryModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Zeiteintrag erstellen
          </Typography>
          <IconButton onClick={closeTimeEntryModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="time-entry-form" onSubmit={handleTimeEntrySubmit}>
            <TextField
              select
              fullWidth
              margin="normal"
              label="Projekt"
              name="project_id"
              value={timeEntryData.project_id}
              onChange={handleTimeEntryChange}
              required
              disabled
            >
              <MenuItem value="">Projekt auswählen</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              margin="normal"
              label="Task"
              name="task_id"
              value={timeEntryData.task_id}
              onChange={handleTimeEntryChange}
              required
              disabled
            >
              <MenuItem value="">Task auswählen</MenuItem>
              {tasks.filter(task => task.project_id === timeEntryData.project_id).map((task) => (
                <MenuItem key={task.id} value={task.id}>
                  {task.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              margin="normal"
              label="Subtask Kommentar"
              name="subtask_comment"
              value={timeEntryData.subtask_comment}
              onChange={handleTimeEntryChange}
              multiline
              rows={3}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Stunden"
              name="hours"
              type="number"
              value={timeEntryData.hours}
              onChange={handleTimeEntryChange}
              inputProps={{ step: 0.25, min: 0.25 }}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Datum"
              name="date"
              type="date"
              value={timeEntryData.date}
              onChange={handleTimeEntryChange}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeTimeEntryModal}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="time-entry-form"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TasksPage;
